import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "./store";
import { getOrgId } from "./orgHelpers";
import { openToast } from "./ToastSlice";
import { InvoiceService } from "../services/WithdrawalService";
import { EnvioForm, IEnvio } from "../models/Envio";

interface EnviosState {
  list: IEnvio[] | null;
  modalAddStatus: boolean;
  creating: boolean;
  loadingResults: boolean;
}

const initialState: EnviosState = {
  list: null,
  modalAddStatus: false,
  creating: false,
  loadingResults: false,
};

const slice = createSlice({
  name: "envios",
  initialState,
  reducers: {
    setList(s, a: PayloadAction<IEnvio[]>) { s.list = a.payload; },
    setModalAddStatus(s, a: PayloadAction<boolean>) { s.modalAddStatus = a.payload; },
    setCreating(s, a: PayloadAction<boolean>) { s.creating = a.payload; },
    setLoadingResults(s, a: PayloadAction<boolean>) { s.loadingResults = a.payload; },
  },
});

export const { setList, setModalAddStatus, setCreating, setLoadingResults } = slice.actions;
export default slice.reducer;

// Lista os invoices (envios) da org e normaliza p/ IEnvio. O backend já ordena
// por createdAt DESC.
export const fetchEnvios = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  dispatch(setLoadingResults(true));
  try {
    const orgId = getOrgId(getState());
    const data: any = (await InvoiceService.getWithdrawalByOrganization(orgId)) ?? [];
    const list: IEnvio[] = (Array.isArray(data) ? data : []).map((i: any) => ({
      id:      i.id,
      client:  i.user,
      asset:   i.asset,
      hash:    i.hash,
      network: i.network,
      value:   i.value,
      status:  i.status,
      date:    i.createdAt,
    }));
    dispatch(setList(list));
  } catch (e) {
    console.error("fetchEnvios error", e);
  } finally {
    dispatch(setLoadingResults(false));
  }
};

// Marca um envio como concluído (status -> COMPLETED).
export const confirmEnvio = (id: number | string): AppThunk<Promise<void>> => async (dispatch) => {
  try {
    await InvoiceService.confirm(id);
    dispatch(openToast({ title: "Envio", message: "Envio marcado como concluído.", effect: "success" }));
    dispatch(fetchEnvios());
  } catch (e: any) {
    const msg = Array.isArray(e?.message) ? e.message.join(" · ") : (e?.message ?? "Falha ao concluir o envio.");
    dispatch(openToast({ title: "Erro ao concluir envio", message: msg, effect: "error" }));
    console.error("confirmEnvio error", e);
  }
};

// Cria o envio (POST /invoices/send) -> debita empresa + cliente.
export const createEnvio = (form: EnvioForm): AppThunk<Promise<void>> => async (dispatch, getState) => {
  dispatch(setCreating(true));
  try {
    const orgId = getOrgId(getState());
    await InvoiceService.createSend({
      organization_id: orgId,
      user_id:         Number(form.user_id),
      asset_id:        Number(form.asset_id),
      value:           String(form.value),
      hash:            form.hash || undefined,
      network_id:      form.network_id ? Number(form.network_id) : undefined,
    });
    dispatch(openToast({ title: "Envio", message: "Envio registrado com sucesso.", effect: "success" }));
    dispatch(setModalAddStatus(false));
    dispatch(fetchEnvios());
  } catch (e: any) {
    const msg = Array.isArray(e?.message) ? e.message.join(" · ") : (e?.message ?? "Falha ao registrar o envio.");
    dispatch(openToast({ title: "Erro ao registrar envio", message: msg, effect: "error" }));
    console.error("createEnvio error", e);
  } finally {
    dispatch(setCreating(false));
  }
};
